import { useQueryClient } from '@tanstack/react-query';
import { useOptimisticMutation, type Variables } from '../../../hooks/use-optimistic-mutation';
import {
  type CustomerGroup,
  type SettingPriceListState,
  useCustomerGroups,
} from '../../customer-groups';
import { type PriceListDeleteError, PriceListError } from '../errors';
import type { PriceListApiResponse } from '../types';
import { QK_ALL_PRICE_LISTS, QK_PAGINATED_PRICE_LISTS } from './use-price-lists';
import { usePriceListsApi } from './use-price-lists-api';

function priceListUsedByGroup(slug: string, customerGroups: CustomerGroup[]) {
  return customerGroups.some((customerGroup) => {
    const priceListSelection = customerGroup.settings.find(
      (setting: { key: string }) => setting.key === 'priceListSelection',
    )?.value as SettingPriceListState | undefined;

    return (
      priceListSelection?.some(
        (list: { price_list_slug: string }) => list.price_list_slug === slug,
      ) ?? false
    );
  });
}

function priceListHasChild(slug: string, priceLists: PriceListApiResponse[]) {
  return priceLists.some((list) => list.rules.some((rule) => rule.source_price_list_slug === slug));
}

export function useDeletePriceList() {
  const { priceListsApi } = usePriceListsApi();
  const { data: customerGroups } = useCustomerGroups();
  const queryClient = useQueryClient();

  async function deleteList(slug: string) {
    if (priceListUsedByGroup(slug, customerGroups)) {
      throw new PriceListError('delete_used_by_group');
    }

    const priceLists = queryClient.getQueryData<PriceListApiResponse[]>(QK_ALL_PRICE_LISTS()) ?? [];
    if (priceListHasChild(slug, priceLists)) {
      throw new PriceListError('delete_has_child');
    }

    try {
      await priceListsApi.priceList(slug).delete();
    } catch {
      throw new PriceListError('delete_failed');
    }
  }

  return useOptimisticMutation<
    PriceListApiResponse[],
    Variables<typeof deleteList>,
    PriceListDeleteError
  >({
    queryClient,
    queryKey: QK_PAGINATED_PRICE_LISTS(),
    invalidateKeys: [QK_ALL_PRICE_LISTS()],
    mutationFn: deleteList,
    optimisticUpdate: (prev, slug) => prev?.filter((list) => list.slug !== slug) ?? [],
  });
}
